import { generateClient } from 'aws-amplify/api';
import { fullTextSearch as fullTextSearchQuery } from '@alucio/aws-beacon-amplify/src/graphql/queries';
import { search } from 'src/utils/logger';

// Timestamp-based warmup to ensure dummy search is executed every 15 minutes
const WARMUP_KEY = 'beacon.search.warmup.timestamp';
const WARMUP_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes in milliseconds

/**
 * Executes a warmup function if 15 minutes have passed since the last execution.
 * Uses sessionStorage to persist the timestamp across provider remounts.
 *
 * @param warmupSearchSilently - Function to execute for warmup
 */
export const runThrottledFunction = async (warmupSearchSilently: () => Promise<void>) => {
  const lastWarmupTime = sessionStorage.getItem(WARMUP_KEY);
  const now = Date.now();
  // If no previous warmup or 15+ minutes have passed, execute warmup
  if (!lastWarmupTime || (now - parseInt(lastWarmupTime, 10)) >= WARMUP_INTERVAL_MS) {
    // Execute first warmup immediately simulating the typehead from the input search box
    warmupSearchSilently().catch(() => {
      search.warmup.error('Search warmup failed');
    });

    // Execute second warmup after 200ms delay simulating the issue with the query from the query string
    setTimeout(() => {
      warmupSearchSilently().catch(() => {
        search.warmup.error('Search warmup failed');
      });
    }, 200);

    sessionStorage.setItem(WARMUP_KEY, now.toString());
  }
};

/**
 * Performs a silent search to warm up the search lambda.
 * This function executes a GraphQL query without affecting the UI state.
 *
 * @param searchText - Text to search for (typically 'test')
 * @param searchAdapterName - Name of the search adapter to use
 */
export const warmupSearchSilently = async (searchText: string, searchAdapterName: string) => {
  try {
    const client = generateClient();
    await client.graphql({
      query: fullTextSearchQuery,
      variables: {
        queryText: searchText,
        filter: {
          statuses: ['PUBLISHED'],
        },
        name: searchAdapterName,
      },
    });
  } catch (error) {
    // Silently ignore errors during warmup
    // eslint-disable-next-line no-console
    search.warmup.error('Search warmup failed:', error);
  }
};
